import React, {useEffect, useMemo, useState, useRef} from "react";
import {useDispatch} from "react-redux";
import {useQueryClient} from '@tanstack/react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  useTheme,
  Chip,
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@material-ui/core";
import {v4 as uuidv4} from "uuid";
import {QRCodeSVG} from "qrcode.react";

import PageContentWrap from "../../../components/Common/PageContentWrap";
import guestmeGatewayImage from "../../../images/guestme_gateway.png";
import {GuestmeNotifications} from "../../../entities";
import {formatPhone, formatPhoneReadable, parsePhone} from "../../../helpers/helpers";
import {gatewayPatternTitles, gatewayRecipientTypes} from "../../../config/constants";
import {socketSendMessage} from "../../../actions/webSocketService/WebSocketActions";
import {useRestaurant, useRestaurantId} from "../../../selectors/RestaurantSelectors";
import {
  useRestaurantGuestmeNotifications,
  useIsWhatsappResetLoading,
  useIsWhatsappLoading,
  useIsTgResetLoading,
  useIsTgPhoneLoading,
  useIsTgCodeLoading,
  useIsTgPasswordLoading,
} from "../../../selectors/GuestmeNotificationsSelectors";
import DisableConfirmationModal from "./DisableConfirmationModal";
import {ContentButton} from "./ContentButton";
import {NotActiveChip} from "./NotActiveChip";
import {
  setWhatsappIsLoading,
  setTgResetIsLoading,
  setTgPhoneIsLoading,
  setTgCodeIsLoading,
  setTgPasswordIsLoading, setWhatsappResetIsLoading,
} from "../../../actions/guestmeNotifications/GuestmeNotificationsActions";
import {updateRestaurantInfo} from "../../../actions/restaurantsInfo/RestaurantsInfoActions";
import {TEventGateway} from "../../../types/ICabinetStore";
import {
  socketSubscribeMessengerAuth,
  socketUnsubscribeMessengerAuth,
} from '../../../actions/webSocketService/WebSocketActions';


// let inputTgPhoneNumberTimeout: number;
const socketChannel = "/user/event/restaurant/messenger/auth";


const ALLOWED_PLACEHOLDERS_SET: Set<string> = new Set([
  "название ресторана",
  "дата",
  "время",
  "сумма",
  "имя",
]);


function validatePatternText(value: string): boolean {
  if (!value) return true;

  const placeholderRegex = /<([^<>]+)>/g;
  let match: RegExpExecArray | null;

  while ((match = placeholderRegex.exec(value)) !== null) {
    const inner = match[1].trim().toLowerCase();
    if (!ALLOWED_PLACEHOLDERS_SET.has(inner)) {
      return false;
    }
  }

  // Проверка на одиночные/некорректные скобки, которые не попали в корректные пары
  const rest = value.replace(placeholderRegex, "");
  return !/[<>]/.test(rest);
}


export function GuestmeNotificationsSettingsWidget({
  onDisable,
} : {
  onDisable: () => void,
}) {
  const restaurantId = useRestaurantId();
  const guestmeNotifications = useRestaurantGuestmeNotifications(restaurantId);
  const isWhatsappResetLoading = useIsWhatsappResetLoading();
  const isWhatsappLoading = useIsWhatsappLoading();
  const isTgResetLoading = useIsTgResetLoading();
  const isTgPhoneLoading = useIsTgPhoneLoading();
  const isTgCodeLoading = useIsTgCodeLoading();
  const isTgPasswordLoading = useIsTgPasswordLoading();
  const isTgLoading = isTgPhoneLoading || isTgCodeLoading || isTgPasswordLoading;

  const prevGatewayStatusRef = useRef<{
    data: GM.GuestmeNotifications.GatewayStatusDTO;
  }>(undefined);

  let refetchInterval = 1000;
  if (!isWhatsappLoading && !isTgLoading) {
    refetchInterval = 1000 * 5;
    if (prevGatewayStatusRef.current) {
      if (prevGatewayStatusRef.current.data?.whatsappAuthStatus === "AUTHORIZED") {
        refetchInterval += 1000 * 15;
      }
      if (prevGatewayStatusRef.current.data?.telegramAuthStatus === "AUTHORIZED") {
        refetchInterval += 1000 * 15;
      }
    }
  }

  const gatewayStatus = GuestmeNotifications.useGatewayStatus(refetchInterval);
  prevGatewayStatusRef.current = gatewayStatus;

  const queryClient = useQueryClient();
  const gatewayStatusQueryKey = GuestmeNotifications.useGatewayStatusQueryKey();

  useEffect(() => {
    if (
      guestmeNotifications?.whatsapp?.type === 'CONNECTION_CLOSED' ||
      guestmeNotifications?.tg?.type === 'CONNECTION_CLOSED'
    ) {
      queryClient.refetchQueries({
        queryKey: gatewayStatusQueryKey,
      });
    }
  }, [guestmeNotifications?.whatsapp?.type, guestmeNotifications?.tg?.type, queryClient]);

  const theme = useTheme();
  const dispatch = useDispatch();
  const currentRestaurant = useRestaurant();
  const {recipients: gatewayRecipients, events: gatewayPatterns} = currentRestaurant.settings.smsNotificationSettings;
  const [inputRecipients, setInputRecipients] = useState<string[]>([]);
  const [inputPatternTexts, setInputPatternTexts] = useState<Partial<Record<TEventGateway, string>>>();
  const [inputPatternTextErrors, setInputPatternTextErrors] = useState<Partial<Record<TEventGateway, string | null>>>();

  useEffect(() => {
    dispatch(socketSubscribeMessengerAuth());

    return () => {
      dispatch(socketUnsubscribeMessengerAuth());
    };
  }, [dispatch]);

  useEffect(() => setInputRecipients(gatewayRecipients), [gatewayRecipients]);

  useEffect(() => {
    let patternTexts: Partial<Record<TEventGateway, string>> = {};
    gatewayPatterns.forEach(pattern => {
      patternTexts[pattern.type] = pattern.template;
    });

    setInputPatternTexts(patternTexts);
  }, [gatewayPatterns]);

  let whatsappState: GM.GuestmeNotifications.WhatsappAuthStatus = 'NOT_AUTHORIZED';
  const validWhatsappStates = ['AUTHORIZED', 'NOT_AUTHORIZED'];
  if (validWhatsappStates.includes(guestmeNotifications?.whatsapp?.whatsapp?.status)) {
    whatsappState = guestmeNotifications.whatsapp.whatsapp.status;
  } else if (validWhatsappStates.includes(gatewayStatus?.data?.whatsappAuthStatus)) {
    whatsappState = gatewayStatus.data.whatsappAuthStatus;
  }

  const whatsappQrCode = guestmeNotifications?.whatsapp?.whatsapp?.qrCode;

  let tgState: GM.GuestmeNotifications.TelegramAuthStatus = 'WAITING_PHONE';
  const validTgStates = ['AUTHORIZED', 'WAITING_PHONE', 'WAITING_CODE', 'WAITING_PASSWORD'];
  if (validTgStates.includes(guestmeNotifications?.tg?.telegram?.status)) {
    tgState = guestmeNotifications.tg.telegram.status;
  } else if (validTgStates.includes(gatewayStatus?.data?.telegramAuthStatus)) {
    tgState = gatewayStatus.data.telegramAuthStatus;
  }

  const [isTgDisableConfirmation, setIsTgDisableConfirmation] = React.useState(false);
  const [isWhatsappDisableConfirmation, setIsWhatsappDisableConfirmation] = React.useState(false);

  const [tgInputPhone, setTgInputPhone] = React.useState("");
  const [tgInputPhoneError, setTgInputPhoneError] = React.useState("");
  const [tgInputCode, setTgInputCode] = React.useState("");
  const [tgInputCodeError, setTgInputCodeError] = React.useState("");
  const [tgInputPassword, setTgInputPassword] = React.useState("");
  const [tgInputPasswordError, setTgInputPasswordError] = React.useState("");

  const whatsappGatewayPhone = useMemo(() => {
    if (!gatewayStatus.data?.whatsappPhone) {
      return "";
    }

    const whatsappGatewayPhoneParsed = parsePhone(gatewayStatus.data.whatsappPhone);
    if (whatsappGatewayPhoneParsed) {
      return formatPhoneReadable(whatsappGatewayPhoneParsed);
    }

    return gatewayStatus.data.whatsappPhone;
  }, [gatewayStatus?.data?.whatsappPhone]);

  const tgGatewayPhone = useMemo(() => {
    if (!gatewayStatus.data?.telegramPhone) {
      return "";
    }

    const tgGatewayPhoneParsed = parsePhone(gatewayStatus.data.telegramPhone);
    if (tgGatewayPhoneParsed) {
      return formatPhoneReadable(tgGatewayPhoneParsed);
    }

    return gatewayStatus.data.telegramPhone;
  }, [gatewayStatus?.data?.telegramPhone]);

  const tgInputPhoneParsed = useMemo(() => {
    if (!tgInputPhone) {
      return null;
    }

    return parsePhone(tgInputPhone);
  }, [tgInputPhone]);

  const isTgInputPhoneValid = !!tgInputPhoneParsed;

  const updateTgInputPhoneError = () => {
    if (tgInputPhoneParsed !== null && !isTgInputPhoneValid) {
      setTgInputPhoneError("Некорректный номер телефона");
      return;
    }

    setTgInputPhoneError("");

    if (tgInputPhoneParsed) {
      setTgInputPhone(formatPhoneReadable(tgInputPhoneParsed));
    }
  };

  const isTgInputCodeValid = useMemo(() => {
    if (tgInputCode === "") {
      return null;
    }

    if (tgInputCode.length !== 5) {
      return false;
    }

    const codeParsed = parseInt(tgInputCode, 10);
    return !isNaN(codeParsed) && codeParsed > 0;
  }, [tgInputCode]);

  const updateTgInputCodeError = () => {
    if (isTgInputCodeValid !== null && !isTgInputCodeValid) {
      setTgInputCodeError("Некорректный формат кода");
      return;
    }

    setTgInputCodeError("");
  };

  const isTgInputPasswordValid = useMemo(() => {
    if (tgInputPassword === "") {
      return null;
    }

    return true;
  }, [tgInputPassword]);

  const updateTgInputPasswordError = () => {
    if (isTgInputPasswordValid !== null && !isTgInputPasswordValid) {
      setTgInputPasswordError("Обязательное поле");
      return;
    }

    setTgInputPasswordError("");
  };

  const sendTgReset = () => {
    dispatch(setTgResetIsLoading(true));

    dispatch(socketSendMessage(socketChannel, {
      uuid: uuidv4(),
      restaurantId,
      messenger: "TELEGRAM",
      type: "RESET",
    }));
  }

  const sendTgPhone = () => {
    if (!tgInputPhoneParsed) {
      return;
    }

    dispatch(setTgPhoneIsLoading(true));

    dispatch(socketSendMessage(socketChannel, {
      uuid: uuidv4(),
      restaurantId,
      messenger: "TELEGRAM",
      type: "SEND_PHONE",
      phone: formatPhone(tgInputPhoneParsed),
    }));
  }

  const sendTgCode = () => {
    if (!tgInputCode || !isTgInputCodeValid) {
      return;
    }

    dispatch(setTgCodeIsLoading(true));

    dispatch(socketSendMessage(socketChannel, {
      uuid: uuidv4(),
      restaurantId,
      messenger: "TELEGRAM",
      type: "SEND_CODE",
      code: parseInt(tgInputCode, 10),
    }));
  }

  const sendTgPassword = () => {
    if (!tgInputPassword || !isTgInputPasswordValid) {
      return;
    }

    dispatch(setTgPasswordIsLoading(true));

    dispatch(socketSendMessage(socketChannel, {
      uuid: uuidv4(),
      restaurantId,
      messenger: "TELEGRAM",
      type: "SEND_PASSWORD",
      password: tgInputPassword,
    }));
  }

  const sendWhatsappAuth = () => {
    dispatch(setWhatsappIsLoading(true));

    dispatch(socketSendMessage(socketChannel, {
      uuid: uuidv4(),
      restaurantId,
      messenger: "WHATSAPP",
      type: "AUTHORIZATION",
    }));
  }

  const sendWhatsappReset = () => {
    dispatch(setWhatsappResetIsLoading(true));

    dispatch(socketSendMessage(socketChannel, {
      uuid: uuidv4(),
      restaurantId,
      messenger: "WHATSAPP",
      type: "RESET",
    }));
  }

  const changeRecipients = (event: React.ChangeEvent<{ value: string }>) => {
    const {value} = event.target;

    // setInputRecipients([value]);

    dispatch(updateRestaurantInfo({
      recipients: [value],
      currentRestaurant,
      type: "smsNotificationSettings",
    }));
  };

  const changePatternAvailability = (patternType: TEventGateway, isEnabled: boolean) => {
    const patterns = gatewayPatterns.map(pattern => {
      if (pattern.type === patternType) {
        return {
          ...pattern,
          isEnabled,
        };
      }

      return {
        ...pattern,
      };
    });

    dispatch(updateRestaurantInfo({
      events: patterns,
      currentRestaurant,
      type: "smsNotificationSettings",
    }));
  };

  const changeLocalPatternText = (patternType: TEventGateway, newValue: string) => {
    setInputPatternTexts((prevState) => ({
      ...prevState,
      [patternType]: newValue,
    }));
  }

  const changePatternText = (patternType: TEventGateway) => {
    const newValue = inputPatternTexts[patternType]
    let isChanged = false;

    const patterns = gatewayPatterns.map(pattern => {
      if (pattern.type === patternType) {
        isChanged = pattern.template !== newValue;

        return {
          ...pattern,
          template: inputPatternTexts[patternType],
        };
      }

      return {
        ...pattern,
      };
    });

    if (isChanged) {
      dispatch(updateRestaurantInfo({
        events: patterns,
        currentRestaurant,
        type: "smsNotificationSettings",
      }));
    }
  };

  const changeLocalPatternTextError = (patternType: TEventGateway, newValue: string | null) => {
    setInputPatternTextErrors((prevState) => ({
      ...prevState,
      [patternType]: newValue,
    }));
  }

  const spinner = <CircularProgress size="1.7em" style={{ color: "darkgray" }} />;

  return (
    <PageContentWrap fullWidth spacing={0}>
      {isTgDisableConfirmation && (
        <DisableConfirmationModal
          handleClose={() => setIsTgDisableConfirmation(false)}
          handleAccept={() => {
            sendTgReset();
            setIsTgDisableConfirmation(false);
          }}
        />
      )}
      {isWhatsappDisableConfirmation && (
        <DisableConfirmationModal
          handleClose={() => setIsWhatsappDisableConfirmation(false)}
          handleAccept={() => {
            sendWhatsappReset();
            setIsWhatsappDisableConfirmation(false);
          }}
        />
      )}
      <Grid item xs={12}>
        <Paper elevation={3} style={{padding: theme.spacing(3)}}>
          <Box display="flex" flexDirection={{xs: "column", md: "row"}}>
            <Box
              display="flex"
              flexDirection="row"
              width={{xs: "100%", md: 330}}
              pr={{xs: 0, md: 3}}
              mr={{xs: 0, md: 3}}
              pb={{xs: 3, md: 0}}
              mb={{xs: 2, md: 0}}
              borderRight={{xs: "none", md: "1px solid rgba(50, 50, 50, 0.25)"}}
              borderBottom={{xs: "1px solid rgba(50, 50, 50, 0.25)", md: "none"}}
            >
              <Box mr={2}>
                <img
                  src={guestmeGatewayImage}
                  alt="Guestme"
                  style={{
                    display: "block",
                    width: theme.spacing(12),
                    height: theme.spacing(12),
                    padding: theme.spacing(3),
                    borderRadius: theme.spacing(1),
                    border: "1px solid #E7EBEE",
                    background: "#F7F8FA",
                  }}
                />
              </Box>
              <Box flexGrow={1}>
                <Typography
                  variant="h4"
                  style={{
                    paddingBottom: 4,
                    marginTop: -theme.spacing(1),
                  }}
                >
                  WhatsApp и&nbsp;Telegram уведомления
                </Typography>

                <Box mb={3}>
                  <Chip
                    label="Услуга подключена"
                    size="small"
                    style={{
                      fontSize: 10,
                      textTransform: "uppercase",
                    }}
                  />
                </Box>

                <ContentButton
                  color="danger"
                  onClick={() => {
                    sendWhatsappReset();
                    sendTgReset();
                    onDisable();
                  }}
                >Отключить</ContentButton>
              </Box>
            </Box>

            <Box flexDirection="column" flexGrow={1}>
              <Box mb={5}>
                <Typography variant="h6" style={{marginBottom: 4}}>
                  Параметры интеграции WhatsApp
                </Typography>

                {whatsappState === "AUTHORIZED" && (
                  <Box
                    borderRadius={4}
                    bgcolor="#E7EBEE"
                    p={2}
                    display="flex"
                    flexDirection="row"
                    gridColumnGap={theme.spacing(1)}
                  >
                    <Box width="60%">
                      <Box mb={2}>
                        <Box fontWeight="bold" mr={2} display="inline-block">
                          Данные авторизации
                        </Box>
                        <Chip
                          label="Активный"
                          color="primary"
                          size="small"
                          style={{ fontSize: 10, textTransform: "uppercase" }}
                        />
                      </Box>
                      <Typography variant="body1">Телефон: {whatsappGatewayPhone || "(не определен)"}</Typography>
                    </Box>
                    <Box width="40%" textAlign="right" alignContent="center">
                      <Box width={135}>
                        <Button
                          variant="contained"
                          style={{
                            paddingLeft: theme.spacing(3),
                            paddingRight: theme.spacing(3),
                            backgroundColor: (!isWhatsappResetLoading && "white") || undefined,
                            color: (!isWhatsappResetLoading && "#F45858") || undefined,
                            width: "100%",
                          }}
                          onClick={() => setIsWhatsappDisableConfirmation(true)}
                          disabled={isWhatsappResetLoading}
                        >
                          {isWhatsappResetLoading ? spinner : 'Отключить'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}

                {whatsappState === "NOT_AUTHORIZED" && (
                  <>
                    <Box mb={3}>
                      <NotActiveChip />
                    </Box>

                    <Typography variant="body1" color="textSecondary" style={{width: "80%"}}>
                      Для подключения сервиса, отсканируйте QR-код
                      приложением WhatsApp в&nbsp;разделе «Связанные устройства»
                      на&nbsp;вашем телефоне.
                    </Typography>

                    <Box
                      mt={3}
                      display="flex"
                      flexDirection="row"
                      gridColumnGap={theme.spacing(4)}
                    >
                      <Box width={200}>
                        {whatsappQrCode ? (
                          <QRCodeSVG
                            value={guestmeNotifications.whatsapp.whatsapp.qrCode}
                            size={120}
                          />
                        ) : (
                          <ContentButton
                            onClick={() => sendWhatsappAuth()}
                            disabled={isWhatsappLoading}
                            style={{ width: "100%" }}
                          >
                            {isWhatsappLoading ? spinner : 'Получить QR-код'}
                          </ContentButton>
                        )}
                      </Box>

                      {(whatsappQrCode || isWhatsappLoading) && (
                        <Box width={135}>
                          <Button
                            variant="contained"
                            style={{
                              paddingLeft: theme.spacing(3),
                              paddingRight: theme.spacing(3),
                              backgroundColor: (!isWhatsappResetLoading && "white") || undefined,
                              color: (!isWhatsappResetLoading && "#F45858") || undefined,
                              width: "100%",
                            }}
                            onClick={() => sendWhatsappReset()}
                            disabled={isWhatsappResetLoading}
                          >
                            {isWhatsappResetLoading ? spinner : 'Отменить'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </Box>

              <Box mb={5}>
                <Typography variant="h6" style={{marginBottom: 4}}>
                  Параметры интеграции Telegram
                </Typography>

                {tgState === "AUTHORIZED" && (
                  <Box
                    borderRadius={4}
                    bgcolor="#E7EBEE"
                    p={2}
                    display="flex"
                    flexDirection="row"
                    gridColumnGap={theme.spacing(1)}
                  >
                    <Box width="60%">
                      <Box mb={2}>
                        <Box
                          fontWeight="bold"
                          mr={2}
                          display="inline-block"
                        >
                          Данные авторизации
                        </Box>
                        <Chip
                          label="Активный"
                          color="primary"
                          size="small"
                          style={{
                            fontSize: 10,
                            textTransform: "uppercase",
                          }}
                        />
                      </Box>
                      <Typography variant="body1">Телефон: {tgGatewayPhone || "(не определен)"}</Typography>
                    </Box>
                    <Box width="40%" textAlign="right" alignContent="center">
                      <Box width={135}>
                        <Button
                          variant="contained"
                          style={{
                            paddingLeft: theme.spacing(3),
                            paddingRight: theme.spacing(3),
                            backgroundColor: (!isTgResetLoading && "white") || undefined,
                            color: (!isTgResetLoading && "#F45858") || undefined,
                            width: "100%",
                          }}
                          onClick={() => setIsTgDisableConfirmation(true)}
                          disabled={isTgResetLoading}
                        >
                          {isTgResetLoading ? spinner : 'Отключить'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}

                {tgState !== "AUTHORIZED" && (
                  <>
                    <Box mb={3}>
                      <NotActiveChip />
                    </Box>

                    <Typography variant="body1" color="textSecondary" style={{width: "80%"}}>
                      Для&nbsp;подключения сервиса, заполните форму ниже
                      и&nbsp;нажмите кнопку «Продолжить».
                    </Typography>
                  </>
                )}

                {tgState === "WAITING_PHONE" && (
                  <>
                    <Box my={3}>
                      <TextField
                        color="secondary"
                        label="Номер телефона"
                        fullWidth
                        value={tgInputPhone}
                        onChange={(e) => {
                          setTgInputPhone(e.target.value);

                          // window.clearTimeout(inputTgPhoneNumberTimeout);
                          // if (!!inputTgPhoneNumberError) {
                          //   inputTgPhoneNumberTimeout = window.setTimeout(() => {
                          //     updateInputTgPhoneNumberError();
                          //   }, 5000);
                          // }
                        }}
                        onBlur={() => {
                          // window.clearTimeout(inputTgPhoneNumberTimeout);
                          updateTgInputPhoneError();
                        }}
                        helperText={tgInputPhoneError}
                        error={!!tgInputPhoneError}
                      />
                    </Box>

                    <Box
                      display="flex"
                      flexDirection="row"
                      gridColumnGap={theme.spacing(4)}
                    >
                      <Box width={135}>
                        <ContentButton
                          disabled={!isTgInputPhoneValid || isTgPhoneLoading}
                          onClick={() => sendTgPhone()}
                          style={{ width: "100%" }}
                        >
                          {isTgPhoneLoading ? spinner : 'Продолжить'}
                        </ContentButton>
                      </Box>

                      {isTgPhoneLoading && (
                        <Box width={135}>
                          <Button
                            variant="contained"
                            style={{
                              paddingLeft: theme.spacing(3),
                              paddingRight: theme.spacing(3),
                              backgroundColor: (!isTgResetLoading && "white") || undefined,
                              color: (!isTgResetLoading && "#F45858") || undefined,
                              width: "100%",
                            }}
                            onClick={() => sendTgReset()}
                            disabled={isTgResetLoading}
                          >
                            {isTgResetLoading ? spinner : 'Отменить'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </>
                )}

                {tgState === "WAITING_CODE" && (
                  <>
                    <Box my={3}>
                      <TextField
                        color="secondary"
                        label="Код верификации"
                        fullWidth
                        value={tgInputCode}
                        onChange={(e) => setTgInputCode(e.target.value)}
                        onBlur={() => updateTgInputCodeError()}
                        helperText={tgInputCodeError}
                        error={!!tgInputCodeError}
                      />
                    </Box>

                    <Box
                      display="flex"
                      flexDirection="row"
                      gridColumnGap={theme.spacing(4)}
                    >
                      <Box width={135}>
                        <ContentButton
                          disabled={!isTgInputCodeValid || isTgCodeLoading}
                          onClick={() => sendTgCode()}
                          style={{ width: "100%" }}
                        >
                          {isTgCodeLoading ? spinner : 'Подключить'}
                        </ContentButton>
                      </Box>
                      <Box width={135}>
                        <Button
                          variant="contained"
                          style={{
                            paddingLeft: theme.spacing(3),
                            paddingRight: theme.spacing(3),
                            backgroundColor: (!isTgResetLoading && "white") || undefined,
                            color: (!isTgResetLoading && "#F45858") || undefined,
                            width: "100%",
                          }}
                          onClick={() => sendTgReset()}
                          disabled={isTgResetLoading}
                        >
                          {isTgResetLoading ? spinner : 'Отменить'}
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}

                {tgState === "WAITING_PASSWORD" && (
                  <>
                    <Box my={3}>
                      <TextField
                        color="secondary"
                        label="Второй фактор (пароль 2FA)"
                        fullWidth
                        value={tgInputPassword}
                        onChange={(e) => setTgInputPassword(e.target.value)}
                        onBlur={() => updateTgInputPasswordError()}
                        helperText={tgInputPasswordError}
                        error={!!tgInputPasswordError}
                      />
                    </Box>

                    <Box
                      display="flex"
                      flexDirection="row"
                      gridColumnGap={theme.spacing(4)}
                    >
                      <Box width={135}>
                        <ContentButton
                          disabled={!isTgInputPasswordValid || isTgPasswordLoading}
                          onClick={() => sendTgPassword()}
                          style={{ width: "100%" }}
                        >
                          {isTgPasswordLoading ? spinner : 'Подключить'}
                        </ContentButton>
                      </Box>
                      <Box width={135}>
                        <Button
                          variant="contained"
                          style={{
                            paddingLeft: theme.spacing(3),
                            paddingRight: theme.spacing(3),
                            backgroundColor: (!isTgResetLoading && "white") || undefined,
                            color: (!isTgResetLoading && "#F45858") || undefined,
                            width: "100%",
                          }}
                          onClick={() => sendTgReset()}
                          disabled={isTgResetLoading}
                        >
                          {isTgResetLoading ? spinner : 'Отменить'}
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>

              <Box mb={0}>
                <Typography variant="h6" style={{marginBottom: theme.spacing(3)}}>
                  Настройка отправки сообщений
                </Typography>

                <Select
                  fullWidth
                  value={(inputRecipients && inputRecipients[0]) || ""}
                  onChange={changeRecipients}
                >
                  {gatewayRecipientTypes.map(messageType => (
                    <MenuItem value={messageType.key} key={messageType.key}>
                      {messageType.value}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper
          elevation={3}
          style={{
            padding: theme.spacing(3),
          }}
        >
          <Typography
            variant="h6"
            style={{
              marginTop: -theme.spacing(1),
              marginBottom: theme.spacing(3),
            }}
          >
            Шаблоны сообщений
          </Typography>

          <Typography
            variant="body1"
            color="textSecondary"
            style={{width: "80%", marginBottom: theme.spacing(1)}}
          >
            Вы можете задавать переменные в&nbsp;текстах сообщений:
          </Typography>

          <Typography variant="body1" color="textSecondary" style={{width: "80%"}}>
            <b>&lt;дата&gt;</b>&nbsp;&mdash; дата резерва,{' '}
            <b>&lt;время&gt;</b>&nbsp;&mdash; время резерва,{' '}
            <b>&lt;сумма&gt;</b>&nbsp;&mdash; сумма чека,{' '}
            <b>&lt;название ресторана&gt;</b>&nbsp;&mdash; название ресторана,{' '}
            <b>&lt;имя&gt;</b>&nbsp;&mdash; имя и&nbsp;фамилия.
          </Typography>

          <Box my={5}>
            {inputPatternTexts && gatewayPatterns.map((pattern, index) => {
              const isLast = index === gatewayPatterns.length - 1;

              const textError =
                inputPatternTextErrors &&
                Object.hasOwn(inputPatternTextErrors, pattern.type) &&
                inputPatternTextErrors[pattern.type];

              return (
                <Box
                  key={pattern.type}
                  display="flex"
                  flexDirection={{
                    xs: "column",
                    md: "row",
                  }}
                  borderBottom={isLast ? null : "1px solid #DEDEDE"}
                  pb={isLast ? 0 : 3}
                  mb={isLast ? 0 : 3}
                >
                  <Box
                    width={{
                      xs: "100%",
                      md: "40%",
                    }}
                    pr={{
                      xs: 0,
                      md: 3,
                    }}
                  >
                    <FormControlLabel
                      label={gatewayPatternTitles[pattern.type]}
                      control={
                        <Switch
                          color="primary"
                          checked={pattern.isEnabled}
                          onChange={(e, newValue) => {
                            changePatternAvailability(pattern.type, newValue);
                          }}
                        />
                      }
                    />
                  </Box>
                  {!!pattern.template && (
                    <Box
                      width={{
                        xs: "100%",
                        md: "60%",
                      }}
                      mt={{
                        xs: 3,
                        md: 0,
                      }}
                    >
                      <TextField
                        color="secondary"
                        label="Сообщение"
                        fullWidth
                        disabled={!pattern.isEnabled}
                        value={inputPatternTexts[pattern.type]}
                        helperText={textError}
                        error={!!textError}
                        onChange={(e) => {
                          changeLocalPatternText(pattern.type, e.target.value);
                        }}
                        onBlur={() => {
                          if (validatePatternText(inputPatternTexts[pattern.type])) {
                            changeLocalPatternTextError(pattern.type, null);
                            changePatternText(pattern.type);
                          } else {
                            changeLocalPatternTextError(
                              pattern.type,
                              "В шаблоне сообщения не верно указана переменная",
                            );
                          }
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Grid>
    </PageContentWrap>
  );
}
